import { isLayoutField } from '@core-engine/schema/constants.js'
import {
  flattenFields,
  getAllFormSchemaFields,
  getManyToManyFields
} from '@core-engine/schema/field-helpers/index.js'
import { normalizeEntitySchema } from '@core-engine/schema/schema-reader.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import type {
  FormField,
  FormSchema,
  GeneratedFile,
  Schema,
  SchemaField
} from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import {
  pluralize,
  singularize,
  singularizeLabel,
  toCamelCase,
  toKebabCase,
  toPascalCase
} from '@core-engine/utils/string.js'
import {
  createAuthorshipFields,
  isAuthorshipFieldName
} from '@next-adapter/generators/actions/action-helpers.js'
import { getEntityFieldContext } from '@next-adapter/generators/entity-filters.js'

type DevModeSnippetLanguage = 'tsx' | 'ts' | 'json'
type DevModeSchemaType = 'entity' | 'single' | 'form'
type DevModeSnippetIcon =
  | 'eye'
  | 'file-code'
  | 'inbox'
  | 'list'
  | 'pencil'
  | 'plus'
  | 'save'
  | 'send'
  | 'trash'
type DevModeSnippetKind = 'jsx' | 'query' | 'mutation'
type DevModeLifecycleHookPhase = 'before' | 'after'
type DevModeLifecycleHookEvent =
  | 'beforeCreate'
  | 'afterCreate'
  | 'beforeUpdate'
  | 'afterUpdate'
  | 'beforeDelete'
  | 'afterDelete'
  | 'beforePublish'
  | 'afterPublish'
  | 'beforeUnpublish'
  | 'afterUnpublish'

interface DevModeSnippet {
  id: string
  label: string
  kind: DevModeSnippetKind
  icon: DevModeSnippetIcon
  language: DevModeSnippetLanguage
  code: string
  codeTabs?: DevModeSnippetCodeTab[]
  payloadExample?: DevModeSnippetExample
  responseExample?: DevModeSnippetExample
}

interface DevModeSnippetCodeTab {
  id: string
  label: string
  language: DevModeSnippetLanguage
  code: string
}

interface DevModeSnippetExample {
  label: string
  language: DevModeSnippetLanguage
  code: string
}

interface DevModeLifecycleHookExample {
  id: string
  label: string
  event: DevModeLifecycleHookEvent
  phase: DevModeLifecycleHookPhase
  icon: DevModeSnippetIcon
  language: DevModeSnippetLanguage
  code: string
  contextExample: DevModeSnippetExample
}

export interface DevModeIntegrationArtifact {
  exportName: string
  importPath: string
  buttonExportName: string
  buttonImportPath: string
  files: GeneratedFile[]
}

const ENTITY_ACTION_FIELD_EXCLUSIONS = new Set(['id', 'createdAt', 'updatedAt', 'sortOrder'])
const SINGLE_ACTION_FIELD_EXCLUSIONS = new Set(['id', 'createdAt', 'updatedAt'])
const DEFAULT_ADMIN_IMPORT_ALIAS = '@admin'
const SAMPLE_UUID = '00000000-0000-4000-8000-000000000001'
const SINGLETON_ID = '00000000-0000-0000-0000-000000000001'
const SAMPLE_FORMATTED_TIMESTAMP = 'Jan 01, 2026'

function isActionField(field: SchemaField, exclusions: Set<string>): boolean {
  return (
    !field.primaryKey &&
    !exclusions.has(field.name) &&
    !isAuthorshipFieldName(field.name) &&
    !isLayoutField(field.type)
  )
}

function formatPropertyName(name: string): string {
  return /^[A-Za-z_$][\w$]*$/.test(name) ? name : JSON.stringify(name)
}

function formatJsonValue(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`
}

function createExample(label: string, value: unknown): DevModeSnippetExample {
  return {
    label,
    language: 'json',
    code: formatJsonValue(value)
  }
}

function isScalarIdentifierPayload(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  )
}

function createPayloadExample(value: unknown): DevModeSnippetExample | undefined {
  if (isScalarIdentifierPayload(value)) {
    return undefined
  }

  return createExample('Payload', value)
}

function createResponseExample(value: unknown): DevModeSnippetExample {
  return createExample('Response', value)
}

function toSampleArrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [value]
}

function formatTsValue(value: unknown, indent = 0): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]'
    if (value.every((item) => item === null || typeof item !== 'object')) {
      return `[${value.map((item) => formatTsValue(item, indent)).join(', ')}]`
    }

    const childIndent = ' '.repeat(indent + 2)
    const currentIndent = ' '.repeat(indent)
    return `[\n${value
      .map((item) => `${childIndent}${formatTsValue(item, indent + 2)}`)
      .join(',\n')}\n${currentIndent}]`
  }

  if (value && typeof value === 'object') {
    return formatTsObject(value as Record<string, unknown>, indent)
  }

  return JSON.stringify(value)
}

function formatTsObject(value: Record<string, unknown>, indent = 0): string {
  const entries = Object.entries(value)
  if (entries.length === 0) return '{}'

  const childIndent = ' '.repeat(indent + 2)
  const currentIndent = ' '.repeat(indent)
  return `{\n${entries
    .map(
      ([key, entryValue]) =>
        `${childIndent}${formatPropertyName(key)}: ${formatTsValue(entryValue, indent + 2)}`
    )
    .join(',\n')}\n${currentIndent}}`
}

function sampleSchemaFieldValue(field: SchemaField): unknown {
  if (field.type === 'select') {
    if (field.default !== undefined) {
      return field.multiple ? toSampleArrayValue(field.default) : field.default
    }

    if (field.options && field.options.length > 0) {
      const firstValue = field.options[0].value
      return field.multiple ? [firstValue] : firstValue
    }

    const exampleValue = `Example ${field.label ?? field.name}`
    return field.multiple ? [exampleValue] : exampleValue
  }

  if (field.default !== undefined) {
    return field.default
  }

  switch (field.type) {
    case 'boolean':
      return true
    case 'email':
      return 'developer@example.com'
    case 'number':
    case 'decimal':
      return 1
    case 'uuid':
    case 'serial':
      return SAMPLE_UUID
    case 'date':
      return '2026-01-01'
    case 'timestamp':
      return '2026-01-01T00:00:00.000Z'
    case 'time':
      return '09:00'
    case 'image':
    case 'video':
    case 'media':
      return 'media-id'
    case 'gallery':
      return ['media-id']
    case 'icon':
      return 'FileText'
    case 'relationship':
      if (isUserRelationshipTarget(field.relationship)) {
        return field.multiple ? ['user-id'] : 'user-id'
      }
      return field.multiple ? [SAMPLE_UUID] : SAMPLE_UUID
    case 'group':
      if (field.fields && field.fields.length > 0) {
        return Object.fromEntries(
          field.fields
            .filter((nestedField) => nestedField.name && !isLayoutField(nestedField.type))
            .map((nestedField) => [nestedField.name, sampleSchemaFieldValue(nestedField)])
        )
      }
      return {}
    case 'list':
      if (field.fields && field.fields.length > 0) {
        return [
          Object.fromEntries(
            field.fields
              .filter((nestedField) => nestedField.name && !isLayoutField(nestedField.type))
              .map((nestedField) => [nestedField.name, sampleSchemaFieldValue(nestedField)])
          )
        ]
      }
      return ['Example item']
    default:
      return `Example ${field.label ?? field.name}`
  }
}

function sampleFormFieldValue(field: FormField): unknown {
  if (field.defaultValue !== undefined) {
    return field.defaultValue
  }

  if (field.options && field.options.length > 0) {
    const firstValue = field.options[0].value
    return field.type === 'multiselect' ? [firstValue] : firstValue
  }

  switch (field.type) {
    case 'email':
      return 'developer@example.com'
    case 'phone':
      return '+14155552671'
    case 'number':
      return 1
    case 'url':
      return 'https://example.com'
    case 'date':
      return '2026-01-01'
    case 'checkbox':
      return true
    case 'multiselect':
      return ['example']
    case 'file':
    case 'upload':
      return 'https://example.com/file.pdf'
    case 'timezone':
      return 'America/New_York'
    case 'list':
      if (field.fields && field.fields.length > 0) {
        return [
          Object.fromEntries(
            field.fields
              .filter((nestedField): nestedField is FormField & { name: string } =>
                Boolean(nestedField.name)
              )
              .map((nestedField) => [nestedField.name, sampleFormFieldValue(nestedField)])
          )
        ]
      }
      return ['Example item']
    default:
      return `Example ${field.label ?? field.name ?? 'value'}`
  }
}

function createInputObject(fields: SchemaField[]): Record<string, unknown> {
  return Object.fromEntries(fields.map((field) => [field.name, sampleSchemaFieldValue(field)]))
}

function createFormInputObject(fields: FormField[]): Record<string, unknown> {
  return Object.fromEntries(
    fields
      .filter((field): field is FormField & { name: string } => Boolean(field.name))
      .map((field) => [field.name, sampleFormFieldValue(field)])
  )
}

function getHtmlOutputFields(fields: SchemaField[]): SchemaField[] {
  return fields.filter((field) => {
    return (field.type === 'richtext' || field.type === 'markdown') && field.output === 'html'
  })
}

function sampleAdminMedia(): Record<string, unknown> {
  return {
    id: 'media-id',
    url: 'https://example.com/media.jpg',
    filename: 'media.jpg',
    contentType: 'image/jpeg',
    size: 102400,
    width: 1200,
    height: 800,
    alt: 'Example media',
    tags: ['example'],
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
    createdAtFormatted: SAMPLE_FORMATTED_TIMESTAMP,
    updatedAtFormatted: SAMPLE_FORMATTED_TIMESTAMP,
    createdBy: 'user-id',
    updatedBy: 'user-id'
  }
}

function sampleAdminUserSummary(): Record<string, unknown> {
  return {
    id: 'user-id',
    name: 'Admin User',
    email: 'admin@example.com'
  }
}

function sampleSchemaOutputValue(field: SchemaField): unknown {
  if (field.name === 'id') return SAMPLE_UUID
  if (field.name === 'createdAt' || field.name === 'updatedAt') return '2026-01-01T00:00:00.000Z'
  if (field.name === 'createdBy' || field.name === 'updatedBy') return 'user-id'

  if (field.type === 'select') {
    const sampleValue = sampleSchemaFieldValue(field)
    const values = Array.isArray(sampleValue) ? sampleValue : sampleValue ? [sampleValue] : []

    return values
      .filter((value): value is string => typeof value === 'string' && value.length > 0)
      .map((value) => ({
        label: field.options?.find((option) => option.value === value)?.label ?? value,
        value
      }))
  }

  if (field.type === 'relationship' && field.relationship && !field.multiple) {
    if (isUserRelationshipTarget(field.relationship)) {
      return {
        id: 'user-id',
        name: 'Example User',
        email: 'user@example.com'
      }
    }

    return {
      id: SAMPLE_UUID,
      name: 'Example related record',
      title: 'Example related record',
      slug: 'example-related-record',
      label: 'Example related record'
    }
  }

  if (field.type === 'group' && field.fields && field.fields.length > 0) {
    return Object.fromEntries(
      field.fields
        .filter((nestedField) => nestedField.name && !isLayoutField(nestedField.type))
        .map((nestedField) => [nestedField.name, sampleSchemaOutputValue(nestedField)])
    )
  }

  if (field.type === 'list' && field.fields && field.fields.length > 0) {
    return [
      Object.fromEntries(
        field.fields
          .filter((nestedField) => nestedField.name && !isLayoutField(nestedField.type))
          .map((nestedField) => [nestedField.name, sampleSchemaOutputValue(nestedField)])
      )
    ]
  }

  return sampleSchemaFieldValue(field)
}

function sampleSingleOutputValue(field: SchemaField): unknown {
  if (field.name === 'id') return SINGLETON_ID

  return sampleSchemaOutputValue(field)
}

function createEntityDataObject(schema: Schema): Record<string, unknown> {
  const { allDbFields, regularDbFields } = getEntityFieldContext(schema)
  const htmlOutputFields = getHtmlOutputFields(regularDbFields)
  const manyToManyFields = getManyToManyFields(schema.fields)
  const mediaFields = regularDbFields.filter((field) => {
    return field.type === 'image' || field.type === 'video' || field.type === 'media'
  })
  const galleryFields = regularDbFields.filter((field) => field.type === 'gallery')

  return addFormattedTimestamps({
    ...Object.fromEntries(allDbFields.map((field) => [field.name, sampleSchemaOutputValue(field)])),
    ...Object.fromEntries(
      htmlOutputFields.map((field) => [
        `${field.name}Html`,
        `<p>${sampleSchemaFieldValue(field)}</p>`
      ])
    ),
    ...Object.fromEntries(
      manyToManyFields.map((field) => [
        field.name,
        isUserRelationshipTarget(field.relationship) ? ['user-id'] : [SAMPLE_UUID]
      ])
    ),
    ...Object.fromEntries(mediaFields.map((field) => [`${field.name}Media`, sampleAdminMedia()])),
    ...Object.fromEntries(
      galleryFields.map((field) => [`${field.name}Media`, [sampleAdminMedia()]])
    ),
    createdByUser: sampleAdminUserSummary(),
    updatedByUser: sampleAdminUserSummary()
  })
}

function addFormattedTimestamps(data: Record<string, unknown>): Record<string, unknown> {
  const output = { ...data }

  if (typeof output.createdAt === 'string') {
    output.createdAtFormatted = SAMPLE_FORMATTED_TIMESTAMP
  }

  if (typeof output.updatedAt === 'string') {
    output.updatedAtFormatted = SAMPLE_FORMATTED_TIMESTAMP
  }

  return output
}

function getSingleAllDbFields(schema: Schema): SchemaField[] {
  const dbFields = flattenFields(schema.fields).filter(
    (field) =>
      !(field.type === 'relationship' && field.multiple === true) && !isLayoutField(field.type)
  )
  const allDbFields = [...dbFields]

  if (!allDbFields.some((field) => field.name === 'createdAt')) {
    allDbFields.push({ name: 'createdAt', type: 'timestamp' as const, required: true })
  }
  if (!allDbFields.some((field) => field.name === 'updatedAt')) {
    allDbFields.push({ name: 'updatedAt', type: 'timestamp' as const, required: true })
  }
  for (const field of createAuthorshipFields()) {
    if (!allDbFields.some((dbField) => dbField.name === field.name)) {
      allDbFields.push(field)
    }
  }

  return allDbFields
}

function createSingleDataObject(schema: Schema): Record<string, unknown> {
  const dbFields = getSingleAllDbFields(schema)
  const htmlOutputFields = getHtmlOutputFields(dbFields)

  return addFormattedTimestamps({
    ...Object.fromEntries(dbFields.map((field) => [field.name, sampleSingleOutputValue(field)])),
    ...Object.fromEntries(
      htmlOutputFields.map((field) => [
        `${field.name}Html`,
        `<p>${sampleSchemaFieldValue(field)}</p>`
      ])
    ),
    createdByUser: sampleAdminUserSummary(),
    updatedByUser: sampleAdminUserSummary()
  })
}

function createFormSubmissionObject(fields: FormField[]): Record<string, unknown> {
  return {
    id: SAMPLE_UUID,
    ...createFormInputObject(fields),
    ipAddress: '127.0.0.1',
    userAgent: 'Mozilla/5.0',
    submittedAt: '2026-01-01T00:00:00.000Z',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z'
  }
}

function resolveActionModulePath(
  actionImportPath: string,
  adminImportAlias = DEFAULT_ADMIN_IMPORT_ALIAS
): string {
  return `${adminImportAlias}/actions/${actionImportPath}`
}

function createSnippetImportName(snippetId: string): string {
  return `${toCamelCase(snippetId)}Snippet`
}

function createLifecycleHookImportName(hookId: string): string {
  return `${toCamelCase(hookId)}LifecycleHook`
}

function createDevModeIntegrationArtifact(
  schema: Schema | FormSchema,
  schemaType: DevModeSchemaType,
  adminDir: string,
  snippets: DevModeSnippet[],
  lifecycleHooks: DevModeLifecycleHookExample[] = [],
  adminImportAlias = DEFAULT_ADMIN_IMPORT_ALIAS
): DevModeIntegrationArtifact {
  const exportName = `${toCamelCase(schema.name)}DevModeIntegration`
  const loadExportName = `load${toPascalCase(schema.name)}DevModeIntegration`
  const buttonExportName = `${toPascalCase(schema.name)}DevModeIntegrateButton`
  const integrationDir = `${adminDir}/utils/dev-mode/${schema.name}`
  const dynamicImportNames = [
    'schemaModule',
    ...snippets.map((snippet) => `${createSnippetImportName(snippet.id)}Module`),
    ...lifecycleHooks.map((hook) => `${createLifecycleHookImportName(hook.id)}Module`)
  ]
  const dynamicImports = [
    `    import('${adminImportAlias}/schemas/${schema.name}.json')`,
    ...snippets.map((snippet) => `    import('./snippets/${snippet.id}.json')`),
    ...lifecycleHooks.map((hook) => `    import('./lifecycle-hooks/${hook.id}.json')`)
  ].join(',\n')
  const snippetValues = snippets
    .map((snippet) => `    ${createSnippetImportName(snippet.id)}Module.default`)
    .join(',\n')
  const lifecycleHookValues = lifecycleHooks
    .map((hook) => `    ${createLifecycleHookImportName(hook.id)}Module.default`)
    .join(',\n')
  const integrationContent = `import type {
  DevModeIntegration,
  DevModeIntegrationMetadata,
  DevModeLifecycleHookExample,
  DevModeSnippet
} from '${adminImportAlias}/components/shared/dev-mode/dev-mode-types'

export const ${exportName} = {
  schemaName: ${JSON.stringify(schema.name)},
  schemaLabel: ${JSON.stringify(schema.label)},
  schemaType: ${JSON.stringify(schemaType)}
} satisfies DevModeIntegrationMetadata

export async function ${loadExportName}(): Promise<DevModeIntegration> {
  const [${dynamicImportNames.join(', ')}] = await Promise.all([
${dynamicImports}
  ])
  const snippets = [
${snippetValues}
  ] as DevModeSnippet[]
  const lifecycleHooks = [
${lifecycleHookValues}
  ] as DevModeLifecycleHookExample[]

  return {
    ...${exportName},
    schemaJson: JSON.stringify(schemaModule.default, null, 2),
    snippets,
    lifecycleHooks
  }
}
`
  const buttonContent = `'use client'

import type { Setting } from '${adminImportAlias}/actions/settings'
import { DevModeIntegrateButton } from '${adminImportAlias}/components/shared/dev-mode-integrate'
import { ${exportName}, ${loadExportName} } from './integration'

interface ${buttonExportName}Props {
  initialSettings?: Setting | null
}

export function ${buttonExportName}({ initialSettings }: ${buttonExportName}Props) {
  return (
    <DevModeIntegrateButton
      integration={${exportName}}
      loadIntegration={${loadExportName}}
      initialSettings={initialSettings}
    />
  )
}
`

  return {
    exportName,
    importPath: `${adminImportAlias}/utils/dev-mode/${schema.name}/integration`,
    buttonExportName,
    buttonImportPath: `${adminImportAlias}/utils/dev-mode/${schema.name}/integrate-button`,
    files: [
      createGeneratedFile(`${integrationDir}/integration.ts`, integrationContent),
      createGeneratedFile(`${integrationDir}/integrate-button.tsx`, buttonContent),
      ...snippets.map((snippet) =>
        createGeneratedFile(
          `${integrationDir}/snippets/${snippet.id}.json`,
          `${JSON.stringify(snippet, null, 2)}\n`
        )
      ),
      ...lifecycleHooks.map((hook) =>
        createGeneratedFile(
          `${integrationDir}/lifecycle-hooks/${hook.id}.json`,
          `${JSON.stringify(hook, null, 2)}\n`
        )
      )
    ]
  }
}

export function buildEntityDevModeIntegration(
  schema: Schema,
  adminDir: string,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeIntegrationArtifact {
  const normalizedSchema = normalizeEntitySchema(schema)

  return createDevModeIntegrationArtifact(
    normalizedSchema,
    'entity',
    adminDir,
    buildEntityDevModeSnippets(normalizedSchema, actionImportPath, adminImportAlias),
    buildEntityDevModeLifecycleHooks(normalizedSchema, adminImportAlias),
    adminImportAlias
  )
}

function buildEntityDevModeSnippets(
  schema: Schema,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeSnippet[] {
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)
  const camelSingular = toCamelCase(singular)
  const camelPlural = toCamelCase(plural)
  const entityLabel = singularizeLabel(schema.label)
  const { regularDbFields } = getEntityFieldContext(schema)
  const inputFields = regularDbFields.filter((field) =>
    isActionField(field, ENTITY_ACTION_FIELD_EXCLUSIONS)
  )
  const hasSlugField = regularDbFields.some((field) => field.name === 'slug')
  const createInput: Record<string, unknown> = createInputObject(inputFields)
  const updateInput: Record<string, unknown> = { id: SAMPLE_UUID, ...createInput }
  const actionModulePath = resolveActionModulePath(actionImportPath, adminImportAlias)
  const detailByIdCode = `import { get${Singular}ById } from '${actionModulePath}'

export default async function ${Singular}Detail({ id }: { id: string }) {
  const ${camelSingular} = await get${Singular}ById(id)

  if (!${camelSingular}) {
    return null
  }

  return <pre>{JSON.stringify(${camelSingular}, null, 2)}</pre>
}
`
  const detailBySlugCode = `import { get${Singular}BySlug } from '${actionModulePath}'

export default async function ${Singular}Detail({ slug }: { slug: string }) {
  const ${camelSingular} = await get${Singular}BySlug(slug)

  if (!${camelSingular}) {
    return null
  }

  return <pre>{JSON.stringify(${camelSingular}, null, 2)}</pre>
}
`
  const detailCodeTabs: DevModeSnippetCodeTab[] | undefined = hasSlugField
    ? [
        { id: 'by-id', label: 'By ID', language: 'tsx', code: detailByIdCode },
        { id: 'by-slug', label: 'By Slug', language: 'tsx', code: detailBySlugCode }
      ]
    : undefined

  if (schema.actions?.draft === true) {
    createInput.published = true
    updateInput.published = true
  }

  const entityData = createEntityDataObject(schema)
  const listResponse = { [camelPlural]: [entityData], total: 1 }
  const createResponse = { success: true, [camelSingular]: entityData }
  const deleteResponse = { success: true }

  return [
    {
      id: 'list',
      label: `${schema.label} List`,
      kind: 'query',
      icon: 'list',
      language: 'tsx',
      responseExample: createResponseExample(listResponse),
      code: `import { get${Plural} } from '${actionModulePath}'

export default async function ${Plural}List() {
  const { ${camelPlural}, total } = await get${Plural}({ limit: 10, offset: 0 })

  return (
    <section>
      <h2>{total} ${schema.label.toLowerCase()}</h2>
      <pre>{JSON.stringify(${camelPlural}, null, 2)}</pre>
    </section>
  )
}
`
    },
    {
      id: 'detail',
      label: `${entityLabel} Detail`,
      kind: 'query',
      icon: 'eye',
      language: 'tsx',
      responseExample: createResponseExample(entityData),
      code: detailByIdCode,
      codeTabs: detailCodeTabs
    },
    {
      id: 'create',
      label: `Create ${entityLabel}`,
      kind: 'mutation',
      icon: 'plus',
      language: 'tsx',
      payloadExample: createPayloadExample(createInput),
      responseExample: createResponseExample(createResponse),
      code: `'use client'

import * as React from 'react'
import { create${Singular}, type ${Singular}CreateInput } from '${actionModulePath}'

const input: ${Singular}CreateInput = ${formatTsObject(createInput)}

export function Create${Singular}Button() {
  const [isPending, startTransition] = React.useTransition()

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await create${Singular}(input)
        })
      }}
    >
      Create ${entityLabel.toLowerCase()}
    </button>
  )
}
`
    },
    {
      id: 'update',
      label: `Update ${entityLabel}`,
      kind: 'mutation',
      icon: 'pencil',
      language: 'tsx',
      payloadExample: createPayloadExample(updateInput),
      responseExample: createResponseExample(createResponse),
      code: `'use client'

import * as React from 'react'
import { update${Singular}, type ${Singular}UpdateInput } from '${actionModulePath}'

export function Update${Singular}Button({ id }: { id: string }) {
  const [isPending, startTransition] = React.useTransition()
  const input: ${Singular}UpdateInput = ${formatTsObject(updateInput).replace(`id: "${SAMPLE_UUID}"`, 'id')}

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await update${Singular}(input)
        })
      }}
    >
      Update ${entityLabel.toLowerCase()}
    </button>
  )
}
`
    },
    {
      id: 'delete',
      label: `Delete ${entityLabel}`,
      kind: 'mutation',
      icon: 'trash',
      language: 'tsx',
      payloadExample: createPayloadExample(SAMPLE_UUID),
      responseExample: createResponseExample(deleteResponse),
      code: `'use client'

import * as React from 'react'
import { delete${Singular} } from '${actionModulePath}'

export function Delete${Singular}Button({ id }: { id: string }) {
  const [isPending, startTransition] = React.useTransition()

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await delete${Singular}(id)
        })
      }}
    >
      Delete ${entityLabel.toLowerCase()}
    </button>
  )
}
`
    }
  ]
}

function lifecycleHookId(event: DevModeLifecycleHookEvent): string {
  return event.replace(/([A-Z])/g, '-$1').toLowerCase()
}

function lifecycleHookPhase(event: DevModeLifecycleHookEvent): DevModeLifecycleHookPhase {
  return event.startsWith('before') ? 'before' : 'after'
}

function lifecycleHookIcon(event: DevModeLifecycleHookEvent): DevModeSnippetIcon {
  if (event.endsWith('Create')) return 'plus'
  if (event.endsWith('Update')) return 'pencil'
  if (event.endsWith('Delete')) return 'trash'
  if (event.endsWith('Publish')) return 'send'
  return 'save'
}

function lifecycleHookActionLabel(event: DevModeLifecycleHookEvent): string {
  return event.replace(/([A-Z])/g, ' $1').toLowerCase()
}

function lifecycleHookCode(
  schemaName: string,
  event: DevModeLifecycleHookEvent,
  entityLabel: string,
  adminImportAlias = DEFAULT_ADMIN_IMPORT_ALIAS
): string {
  const actionLabel = lifecycleHookActionLabel(event)

  if (lifecycleHookPhase(event) === 'before') {
    return `import { registerHook } from '${adminImportAlias}/lib/lifecycle-hooks'

registerHook('${schemaName}', '${event}', async (ctx) => {
  console.log('${entityLabel} ${actionLabel} requested', {
    actorId: ctx.user?.id ?? null,
    entityId: ctx.entityId,
    input: ctx.input
  })

  // Return an abort result to stop this mutation.
  // return { abort: true, reason: '${entityLabel} is not ready for this change' }
})
`
  }

  return `import { registerHook } from '${adminImportAlias}/lib/lifecycle-hooks'

registerHook('${schemaName}', '${event}', async (ctx) => {
  await fetch('https://example.com/webhooks/${schemaName}/${lifecycleHookId(event)}', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      actorId: ctx.user?.id ?? null,
      entityType: ctx.entityType,
      entityId: ctx.entityId,
      data: ctx.data
    })
  })
})
`
}

function lifecycleHookContextExample(
  event: DevModeLifecycleHookEvent,
  schemaName: string,
  input: Record<string, unknown>,
  data: Record<string, unknown>,
  schemaType: DevModeSchemaType = 'entity'
): DevModeSnippetExample {
  const phase = lifecycleHookPhase(event)
  const context: Record<string, unknown> = {
    user: sampleAdminUserSummary(),
    entityType: schemaName,
    timestamp: '2026-01-01T00:00:00.000Z'
  }

  if (schemaType === 'form') {
    context.schemaType = 'form'
  }
  if (event !== 'beforeCreate' && event !== 'afterCreate') {
    context.entityId = SAMPLE_UUID
  }
  if (phase === 'before' && !event.endsWith('Delete')) {
    context.input = event.endsWith('Publish')
      ? { ...input, published: true }
      : event.endsWith('Unpublish')
        ? { published: false }
        : input
  }
  if (phase === 'after' && !event.endsWith('Delete')) {
    context.entityId = SAMPLE_UUID
    context.data = data
  }

  return createExample('Context', context)
}

function buildEntityDevModeLifecycleHooks(
  schema: Schema,
  adminImportAlias?: string
): DevModeLifecycleHookExample[] {
  const entityLabel = singularizeLabel(schema.label)
  const { regularDbFields } = getEntityFieldContext(schema)
  const inputFields = regularDbFields.filter((field) =>
    isActionField(field, ENTITY_ACTION_FIELD_EXCLUSIONS)
  )
  const input = createInputObject(inputFields)
  const data = createEntityDataObject(schema)
  const events: DevModeLifecycleHookEvent[] = [
    'beforeCreate',
    'afterCreate',
    'beforeUpdate',
    'afterUpdate',
    'beforeDelete',
    'afterDelete'
  ]

  if (schema.actions?.draft === true) {
    events.push('beforePublish', 'afterPublish', 'beforeUnpublish', 'afterUnpublish')
  }

  return events.map((event) => ({
    id: lifecycleHookId(event),
    label: event,
    event,
    phase: lifecycleHookPhase(event),
    icon: lifecycleHookIcon(event),
    language: 'ts',
    code: lifecycleHookCode(schema.name, event, entityLabel, adminImportAlias),
    contextExample: lifecycleHookContextExample(event, schema.name, input, data)
  }))
}

function buildFormDevModeLifecycleHooks(
  schema: FormSchema,
  adminImportAlias?: string
): DevModeLifecycleHookExample[] {
  const formLabel = schema.label
  const fields = getAllFormSchemaFields(schema)
  const input = createFormInputObject(fields)
  const data = createFormSubmissionObject(fields)
  const events: DevModeLifecycleHookEvent[] = [
    'beforeCreate',
    'afterCreate',
    'beforeDelete',
    'afterDelete'
  ]

  return events.map((event) => ({
    id: lifecycleHookId(event),
    label: event,
    event,
    phase: lifecycleHookPhase(event),
    icon: lifecycleHookIcon(event),
    language: 'ts',
    code: lifecycleHookCode(schema.name, event, formLabel, adminImportAlias),
    contextExample: lifecycleHookContextExample(event, schema.name, input, data, 'form')
  }))
}

export function buildSingleDevModeIntegration(
  schema: Schema,
  adminDir: string,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeIntegrationArtifact {
  return createDevModeIntegrationArtifact(
    schema,
    'single',
    adminDir,
    buildSingleDevModeSnippets(schema, actionImportPath, adminImportAlias),
    [],
    adminImportAlias
  )
}

function buildSingleDevModeSnippets(
  schema: Schema,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeSnippet[] {
  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)
  const camelSingular = toCamelCase(singular)
  const singleLabel = schema.label
  const inputFields = flattenFields(schema.fields)
    .filter((field) => !(field.type === 'relationship' && field.multiple === true))
    .filter((field) => isActionField(field, SINGLE_ACTION_FIELD_EXCLUSIONS))
  const upsertInput = createInputObject(inputFields)
  const actionModulePath = resolveActionModulePath(actionImportPath, adminImportAlias)
  const singleData = createSingleDataObject(schema)
  const upsertResponse = { success: true, [camelSingular]: singleData }

  return [
    {
      id: 'read',
      label: `${singleLabel} Read`,
      kind: 'query',
      icon: 'eye',
      language: 'tsx',
      responseExample: createResponseExample(singleData),
      code: `import { get${Singular} } from '${actionModulePath}'

export default async function ${Singular}Summary() {
  const ${camelSingular} = await get${Singular}()

  if (!${camelSingular}) {
    return null
  }

  return <pre>{JSON.stringify(${camelSingular}, null, 2)}</pre>
}
`
    },
    {
      id: 'upsert',
      label: `Save ${singleLabel}`,
      kind: 'mutation',
      icon: 'save',
      language: 'tsx',
      payloadExample: createPayloadExample(upsertInput),
      responseExample: createResponseExample(upsertResponse),
      code: `'use client'

import * as React from 'react'
import { upsert${Singular}, type ${Singular}UpsertInput } from '${actionModulePath}'

const input: ${Singular}UpsertInput = ${formatTsObject(upsertInput)}

export function Save${Singular}Button() {
  const [isPending, startTransition] = React.useTransition()

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await upsert${Singular}(input)
        })
      }}
    >
      Save ${schema.label.toLowerCase()}
    </button>
  )
}
`
    }
  ]
}

export function buildFormDevModeIntegration(
  schema: FormSchema,
  adminDir: string,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeIntegrationArtifact {
  return createDevModeIntegrationArtifact(
    schema,
    'form',
    adminDir,
    buildFormDevModeSnippets(schema, actionImportPath, adminImportAlias),
    buildFormDevModeLifecycleHooks(schema, adminImportAlias),
    adminImportAlias
  )
}

function buildFormDevModeSnippets(
  schema: FormSchema,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeSnippet[] {
  const pascal = toPascalCase(schema.name)
  const fields = getAllFormSchemaFields(schema)
  const submissionInput = createFormInputObject(fields)
  const actionModulePath = resolveActionModulePath(actionImportPath, adminImportAlias)
  const formComponentModulePath = `${adminImportAlias ?? DEFAULT_ADMIN_IMPORT_ALIAS}/components/forms/${toKebabCase(schema.name)}-form`
  const submissionData = createFormSubmissionObject(fields)
  const submitResponse = { success: true, submission: submissionData }
  const submissionsResponse = { submissions: [submissionData], total: 1 }
  const deleteResponse = { success: true }
  const formLabel = schema.label
  const formComponentCode = `import { ${pascal}Form } from '${formComponentModulePath}'

export default function ${pascal}Page() {
  return <${pascal}Form />
}
`
  const submitActionCode = `'use client'

import * as React from 'react'
import {
  create${pascal}Submission,
  type Create${pascal}SubmissionInput
} from '${actionModulePath}'

const input: Create${pascal}SubmissionInput = ${formatTsObject(submissionInput)}

export function ${pascal}SubmitButton() {
  const [isPending, startTransition] = React.useTransition()

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await create${pascal}Submission(input)
        })
      }}
    >
      Submit ${schema.label.toLowerCase()}
    </button>
  )
}
`

  return [
    {
      id: 'jsx',
      label: `${formLabel} Component`,
      kind: 'jsx',
      icon: 'file-code',
      language: 'tsx',
      code: formComponentCode
    },
    {
      id: 'submit',
      label: `Submit ${formLabel}`,
      kind: 'mutation',
      icon: 'send',
      language: 'tsx',
      payloadExample: createPayloadExample(submissionInput),
      responseExample: createResponseExample(submitResponse),
      code: submitActionCode
    },
    {
      id: 'submissions',
      label: `${formLabel} Submissions`,
      kind: 'query',
      icon: 'inbox',
      language: 'tsx',
      responseExample: createResponseExample(submissionsResponse),
      code: `import { get${pascal}Submissions } from '${actionModulePath}'

export default async function ${pascal}Submissions() {
  const { submissions, total } = await get${pascal}Submissions({ limit: 10, offset: 0 })

  return (
    <section>
      <h2>{total} ${schema.label.toLowerCase()} submissions</h2>
      <pre>{JSON.stringify(submissions, null, 2)}</pre>
    </section>
  )
}
`
    },
    {
      id: 'delete',
      label: `Delete ${formLabel} Submission`,
      kind: 'mutation',
      icon: 'trash',
      language: 'tsx',
      payloadExample: createPayloadExample(SAMPLE_UUID),
      responseExample: createResponseExample(deleteResponse),
      code: `'use client'

import * as React from 'react'
import { delete${pascal}Submission } from '${actionModulePath}'

export function Delete${pascal}SubmissionButton({ id }: { id: string }) {
  const [isPending, startTransition] = React.useTransition()

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await delete${pascal}Submission(id)
        })
      }}
    >
      Delete submission
    </button>
  )
}
`
    }
  ]
}
